import React from "react";

import { useNavigate } from "react-router-dom";

import { activateOnEnterOrSpace } from "../utils/keyboardActivation";

/**
 * Major rivalry pairs across sports.
 * Each entry is [teamA, teamB] using common short names that
 * match what the API returns for homeTeam / awayTeam.
 */
const RIVALRIES = [
  // Soccer - Premier League
  ["Manchester United", "Manchester City"],
  ["Liverpool", "Manchester United"],
  ["Liverpool", "Everton"],
  ["Arsenal", "Tottenham"],
  ["Chelsea", "Arsenal"],
  ["Chelsea", "Tottenham"],
  // Soccer - La Liga
  ["Barcelona", "Real Madrid"],
  ["Atletico Madrid", "Real Madrid"],
  // Soccer - Serie A
  ["AC Milan", "Inter"],
  ["Juventus", "Inter"],
  ["AS Roma", "Lazio"],
  // Soccer - Bundesliga
  ["Bayern Munich", "Borussia Dortmund"],
  // Soccer - MLS
  ["LA Galaxy", "Los Angeles FC"],
  // NBA
  ["Los Angeles Lakers", "Boston Celtics"],
  ["Los Angeles Lakers", "LA Clippers"],
  ["Golden State Warriors", "Los Angeles Lakers"],
  ["New York Knicks", "Brooklyn Nets"],
  ["Chicago Bulls", "Detroit Pistons"],
  ["Miami Heat", "Boston Celtics"],
  ["Philadelphia 76ers", "Boston Celtics"],
  // NFL
  ["Dallas Cowboys", "Washington Commanders"],
  ["Green Bay Packers", "Chicago Bears"],
  ["Pittsburgh Steelers", "Baltimore Ravens"],
  ["New England Patriots", "New York Jets"],
  ["San Francisco 49ers", "Seattle Seahawks"],
  ["Kansas City Chiefs", "Las Vegas Raiders"],
  // NHL
  ["Montreal Canadiens", "Toronto Maple Leafs"],
  ["Boston Bruins", "Montreal Canadiens"],
  ["New York Rangers", "New York Islanders"],
  ["Pittsburgh Penguins", "Philadelphia Flyers"],
  ["Chicago Blackhawks", "Detroit Red Wings"],
  // MLB
  ["New York Yankees", "Boston Red Sox"],
  ["Los Angeles Dodgers", "San Francisco Giants"],
  ["Chicago Cubs", "St. Louis Cardinals"],
  ["New York Yankees", "New York Mets"],
];

/**
 * Check if two team names form a known rivalry.
 * Performs case-insensitive partial matching so "Man Utd" still
 * matches "Manchester United".
 */
const isRivalry = (teamA, teamB) => {
  if (!teamA || !teamB) return false;
  const a = teamA.toLowerCase();
  const b = teamB.toLowerCase();

  return RIVALRIES.some(([r1, r2]) => {
    const r1l = r1.toLowerCase();
    const r2l = r2.toLowerCase();

    return (
      ((a.includes(r1l) || r1l.includes(a)) &&
        (b.includes(r2l) || r2l.includes(b))) ||
      ((a.includes(r2l) || r2l.includes(a)) &&
        (b.includes(r1l) || r1l.includes(b)))
    );
  });
};

/**
 * Normalize a game object from either carousel (formatted) or
 * raw TheSportsDB / API-Sports shapes into a common shape.
 */
const normalizeGame = (game) => {
  const homeTeam =
    game.homeTeam || game.strHomeTeam || game.teams?.home?.name || "";
  const awayTeam =
    game.awayTeam || game.strAwayTeam || game.teams?.away?.name || "";
  const homeTeamLogo =
    game.homeTeamLogo || game.strHomeTeamBadge || game.teams?.home?.logo || "";
  const awayTeamLogo =
    game.awayTeamLogo || game.strAwayTeamBadge || game.teams?.away?.logo || "";

  // Scores - handle various formats
  let homeScore = game.homeTeamScore ?? game.intHomeScore ?? null;
  let awayScore = game.awayTeamScore ?? game.intAwayScore ?? null;

  if (homeScore !== null && homeScore !== undefined) {
    homeScore =
      typeof homeScore === "number" ? homeScore : parseInt(homeScore, 10);
    if (isNaN(homeScore)) homeScore = null;
  }
  if (awayScore !== null && awayScore !== undefined) {
    awayScore =
      typeof awayScore === "number" ? awayScore : parseInt(awayScore, 10);
    if (isNaN(awayScore)) awayScore = null;
  }

  // Determine if live
  const isLive =
    game.rating === "LIVE" ||
    (game.status &&
      game.status.short !== "NS" &&
      game.status.short !== "FT" &&
      game.status.short !== "TBD" &&
      game.status.short !== "PST" &&
      game.status.short !== "AOT" &&
      game.status.short !== "AET");

  const leagueName =
    (typeof game.league === "object" ? game.league?.name : game.league) ||
    game.strLeague ||
    "";

  const eventId = game.id || game.mediaId || game.idEvent;

  // Timestamp for display
  const timestamp =
    game.timestamp || game.strTimestamp || game.date || game.dateEvent || "";

  return {
    id: eventId,
    homeTeam,
    awayTeam,
    homeTeamLogo,
    awayTeamLogo,
    homeScore,
    awayScore,
    isLive,
    leagueName,
    timestamp,
    // Preserve the original game for navigation state
    _original: game,
  };
};

/**
 * Score a game for "Games to Watch" ranking.
 *
 * Points breakdown:
 * - Rivalry match           +100
 * - Live + close score      +90
 * - Followed team playing   +80
 * - Default (league name)   +50
 */
const scoreGame = (normalized, favoriteTeams) => {
  let score = 0;
  let reason = normalized.leagueName || "Featured";

  // Rivalry detection
  if (isRivalry(normalized.homeTeam, normalized.awayTeam)) {
    score += 100;
    reason = "Rivalry match";
  }

  // Live + close score
  if (
    normalized.isLive &&
    normalized.homeScore !== null &&
    normalized.awayScore !== null
  ) {
    const diff = Math.abs(normalized.homeScore - normalized.awayScore);

    if (diff <= 3) {
      score += 90;
      reason = "Close game \u2014 LIVE";
    }
  }

  // Followed team
  const teams = (favoriteTeams || []).map((t) => t.toLowerCase());
  const homeMatch = teams.some(
    (t) =>
      normalized.homeTeam.toLowerCase().includes(t) ||
      t.includes(normalized.homeTeam.toLowerCase())
  );
  const awayMatch = teams.some(
    (t) =>
      normalized.awayTeam.toLowerCase().includes(t) ||
      t.includes(normalized.awayTeam.toLowerCase())
  );

  if (homeMatch || awayMatch) {
    score += 80;
    // Only override reason if we don't already have a higher-value one
    if (score <= 80) {
      reason = "Your team is playing";
    }
  }

  // Default minimum
  if (score === 0) {
    score = 50;
  }

  return { score, reason };
};

/**
 * Format a timestamp for display.
 */
const formatGameTime = (timestamp) => {
  if (timestamp == null) return "";

  try {
    let date;

    if (typeof timestamp === "number") {
      const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;

      date = new Date(ms);
    } else {
      const tsStr = String(timestamp);
      const ts =
        tsStr.endsWith("Z") || tsStr.includes("+") || tsStr.includes("-")
          ? tsStr
          : `${tsStr}Z`;

      date = new Date(ts);
    }

    if (isNaN(date.getTime())) return "";

    const now = new Date();
    const diffMs = date - now;
    const diffHours = diffMs / (1000 * 60 * 60);

    // If game is within the next 24 hours, show relative time
    if (diffHours > 0 && diffHours < 24) {
      if (diffHours < 1) {
        const mins = Math.round(diffHours * 60);

        return `In ${mins}m`;
      }

      return `In ${Math.round(diffHours)}h`;
    }

    // Otherwise show date + time
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const GamesToWatch = ({ games, favoriteTeams }) => {
  const navigate = useNavigate();

  if (!games || games.length === 0) return null;

  // Deduplicate by event id before scoring
  const seen = new Set();
  const unique = games.filter((g) => {
    const id = g.id || g.mediaId || g.idEvent;

    if (!id || seen.has(id)) return false;
    seen.add(id);

    return true;
  });

  // Normalize, score, sort, take top 5
  const scored = unique
    .map((g) => {
      const normalized = normalizeGame(g);
      const { score, reason } = scoreGame(normalized, favoriteTeams);

      return { ...normalized, score, reason };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  if (scored.length === 0) return null;

  const handleGameClick = (game) => {
    const eventId = game.id;
    const orig = game._original;

    const eventData = {
      id: orig.id || orig.mediaId,
      mediaId: orig.mediaId || orig.id,
      name: orig.name || orig.title || `${game.homeTeam} vs ${game.awayTeam}`,
      sport: orig.sport || orig.genre,
      strHomeTeam: game.homeTeam,
      strAwayTeam: game.awayTeam,
      strHomeTeamBadge: game.homeTeamLogo,
      strAwayTeamBadge: game.awayTeamLogo,
      intHomeScore: game.homeScore,
      intAwayScore: game.awayScore,
      homeTeamScore: game.homeScore,
      awayTeamScore: game.awayScore,
      strLeague: game.leagueName,
      strLeagueBadge: orig.leagueLogo || orig.league?.logo || "",
      strVenue: orig.venue || "",
      strThumb: orig.backdrop || orig.poster || "",
      backdrop: orig.backdrop || "",
      posterPath: orig.poster || orig.posterPath || "",
      thumbnail: orig.poster || orig.thumbnail || "",
      strSport: orig.genre || orig.categoryName || "",
      categoryName: orig.genre || orig.categoryName || "",
      timestamp: game.timestamp,
      strTimestamp: game.timestamp,
    };

    navigate(`/web-player/sports/event/${eventId}`, {
      state: { event: eventData },
    });
  };

  return (
    <div className="px-24 mb-12">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-white flex items-center gap-3">
          <span aria-label="fire" role="img">
            {"\uD83D\uDD25"}
          </span>
          Games to Watch
        </h2>
        <button
          className="text-blue-400 hover:text-blue-300 text-sm font-semibold transition-colors duration-200"
          onClick={() => navigate("/web-player/sports/scores")}
        >
          See All <i className="fas fa-chevron-right ml-1 text-xs" />
        </button>
      </div>

      {/* Game Cards - Vertical List */}
      <div className="flex flex-col gap-3">
        {scored.map((game, index) => {
          const timeDisplay = game.isLive
            ? null
            : formatGameTime(game.timestamp);

          return (
            <div
              className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-500/40 rounded-xl p-4 cursor-pointer transition-all duration-300"
              key={game.id || index}
              role="button"
              tabIndex={0}
              onClick={() => handleGameClick(game)}
              onKeyDown={activateOnEnterOrSpace}
            >
              <div className="flex items-center justify-between">
                {/* Teams */}
                <div className="flex items-center gap-6 flex-1 min-w-0">
                  {/* Home Team */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {game.homeTeamLogo ? (
                      <img
                        alt={game.homeTeam}
                        className="w-8 h-8 object-contain flex-shrink-0 team-logo"
                        src={game.homeTeamLogo}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-white/60 text-xs font-bold">
                          {game.homeTeam?.charAt(0) || "?"}
                        </span>
                      </div>
                    )}
                    <span className="text-white font-medium text-sm truncate">
                      {game.homeTeam}
                    </span>
                  </div>

                  {/* VS / Score */}
                  <div className="flex-shrink-0 text-center min-w-[60px]">
                    {game.isLive &&
                    game.homeScore !== null &&
                    game.awayScore !== null ? (
                      <span className="text-white font-bold text-lg">
                        {game.homeScore} - {game.awayScore}
                      </span>
                    ) : (
                      <span className="text-white/40 text-sm font-medium">
                        vs
                      </span>
                    )}
                  </div>

                  {/* Away Team */}
                  <div className="flex items-center gap-3 flex-1 min-w-0 justify-end">
                    <span className="text-white font-medium text-sm truncate text-right">
                      {game.awayTeam}
                    </span>
                    {game.awayTeamLogo ? (
                      <img
                        alt={game.awayTeam}
                        className="w-8 h-8 object-contain flex-shrink-0 team-logo"
                        src={game.awayTeamLogo}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-white/60 text-xs font-bold">
                          {game.awayTeam?.charAt(0) || "?"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right side - Badge & Time */}
                <div className="flex items-center gap-3 ml-6 flex-shrink-0">
                  {/* Reason Badge */}
                  <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    {game.reason}
                  </span>

                  {/* Live indicator or time */}
                  {game.isLive ? (
                    <span className="bg-red-600 text-white text-xs px-2 py-1 rounded font-bold uppercase animate-pulse">
                      LIVE
                    </span>
                  ) : (
                    timeDisplay && (
                      <span className="text-white/50 text-xs whitespace-nowrap">
                        {timeDisplay}
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GamesToWatch;
